import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { getEntityDisplayName } from '../../../../extViewmodels/Utils';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useBlogApprovalRequestMutation } from '../../../../queries/Blog/useBlogApprovalRequestMutation';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IModalContext<boolean> {
	blogReportViews?: Api.IBlogReportView[];
	styles?: StyleDeclarationValue[];
}

function BlogApprovalReminderComposerBase({ blogReportViews, parentModal, styles }: IProps) {
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging('BlogApprovalReminderComposer');

	const [isBusy, setIsBusy] = React.useState<boolean>(false);
	const [note, setNote] = React.useState<string>(() => {
		let value = 'Hi,';
		if (blogReportViews?.length === 1 && blogReportViews?.[0]?.status === Api.BlogStatus.Pending) {
			value = `Hi ${blogReportViews[0].approvalRequests[0].approver?.firstName},`;
		}
		return value;
	});

	const approvalRequestMutation = useBlogApprovalRequestMutation();

	const onTextChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setNote(event.target.value || '');
	};

	const sendReminder = () => {
		setIsBusy(true);
		const promises = blogReportViews.map(blogReportView => {
			const trimmed = (note || '').trim();
			if (trimmed && blogReportView) {
				const content = Api.createRawRichTextContentStateWithText(trimmed);
				if (blogReportView.status === Api.BlogStatus.Pending && content?.document) {
					logEvent('SendReminder');
					return approvalRequestMutation.mutateAsync({
						blogId: blogReportView.id,
						emailContent: content,
					});
				}
			}
		});
		Promise.all(promises)
			.then(() => {
				toast.success(`Reminder email${blogReportViews?.length > 1 ? 's' : ''} sent!`);
				parentModal?.onRequestClose(true, false);
			})
			.catch((error: Api.IOperationResultNoValue) => {
				logApiError('SendReminder-Error', error);
				errorMessages.pushApiError(error);
			})
			.finally(() => {
				setIsBusy(false);
			});
	};

	return (
		<div className={css(styleSheet.container, ...(styles || []))}>
			<div className={css(styleSheet.title)}>Notify Client</div>
			<div className={css(styleSheet.message)}>
				{`Leave a reminder for the client below. Levitate will send this along with the blogs you've picked out `}
				<b>from your email.</b>
			</div>
			{blogReportViews?.length > 1 ? (
				<div className={css(styleSheet.message)}>
					{blogReportViews.map((blogReportView, i) => {
						return (
							<div key={`contact${i}`}>
								{blogReportView.approvalRequests.length > 0
									? getEntityDisplayName(blogReportView.approvalRequests[0].approver)
									: ''}
							</div>
						);
					})}
				</div>
			) : null}
			<div>
				<DeprecatedTextArea
					autoFocus={true}
					disabled={isBusy}
					inputClassName={css(styleSheet.inputField)}
					inputId='bulk-email-approval-reminder-composer-input'
					onChange={onTextChanged}
					value={note || ''}
				/>
			</div>
			<button className={css([baseStyleSheet.ctaButton, styleSheet.button])} disabled={isBusy} onClick={sendReminder}>
				<span>Send</span>
			</button>
			{isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
		</div>
	);
}

export const BlogApprovalReminderComposer = inject(ModalChildComponentContextKey)(
	observer(BlogApprovalReminderComposerBase)
);

export const BlogApprovalReminderComposerModal = asModalComponent(BlogApprovalReminderComposer, {
	className: `blog-approval-reminder-composer-modal`,
	shouldCloseOnOverlayClick: true,
	useDefaultHeader: true,
});
